import { useEffect, useRef, useCallback } from 'react';
import { useAuditFilterContext } from '../context/AuditFilterContext';
import { TableFilterItem } from 'src/presentations/components/molecules/table/columnHeader';

interface UseAuditDashboardFilterOptions {
  accessorKey: string;
  filterItems: TableFilterItem[];
  initialSelectedValues?: string[];
  enabled?: boolean;
}

/**
 * Special hook for Audit Dashboard that uses AuditFilterContext
 * Replaces useAutoSelectFilter for this specific table
 */
export const useAuditDashboardFilter = ({ accessorKey, filterItems, initialSelectedValues = [], enabled = true }: UseAuditDashboardFilterOptions) => {
  const { state, setAvailable, setSelected } = useAuditFilterContext();
  const hasInitializedRef = useRef(false);

  // Derive current state from context
  // If not in context yet (first render), fallback to empty
  const columnState = state.columns[accessorKey];
  const selectedValues = columnState?.selected || [];

  // 1. Sync available items to context whenever filterItems changes
  useEffect(() => {
    if (!enabled) return;
    const ids = filterItems.map((item) => item.id);
    // This runs on every filterItems change (e.g. lazy loading, search)
    // We update the 'available' list in context so 'Reset All' knows what to select
    setAvailable(accessorKey, ids);
  }, [accessorKey, filterItems, setAvailable, enabled]);

  // 2. Initialization Logic (Run once)
  // Determine initial selection based on props -> Sync to Context
  useEffect(() => {
    if (!enabled || hasInitializedRef.current || !filterItems.length) return;

    // If context already has selection?
    // We prioritize initialSelectedValues from props IF it exists (e.g. from URL/DashboardContext)
    // Otherwise we default to "Select All"
    if (initialSelectedValues.length > 0) {
      setSelected(accessorKey, initialSelectedValues);
    } else {
      // Default: Select All
      setSelected(
        accessorKey,
        filterItems.map((i) => i.id)
      );
    }

    hasInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessorKey, filterItems.length, initialSelectedValues, enabled, setSelected]);

  // 2b. Sync context when initialSelectedValues changes after initialization
  // This handles the case when Dashboard context is cleared (e.g., "Clear All Filters")
  useEffect(() => {
    if (!enabled || !hasInitializedRef.current || !filterItems.length) return;

    // If initialSelectedValues is empty (cleared), reset to Select All
    if (initialSelectedValues.length === 0) {
      setSelected(
        accessorKey,
        filterItems.map((i) => i.id)
      );
    } else {
      // Otherwise sync with Dashboard context values
      setSelected(accessorKey, initialSelectedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedValues.length, accessorKey, filterItems, setSelected, enabled]);

  // 3. Auto-select new items (Infinite Scroll Logic)
  // When filterItems grows, check if we should auto-select the new ones
  const handleAutoSelectNewItems = useCallback(
    (previousLength: number) => {
      if (!enabled || !filterItems.length) return;

      const currentLength = filterItems.length;
      if (currentLength <= previousLength) return;

      // Get IDs of items that existed before
      const previousItemIds = filterItems.slice(0, previousLength).map((item) => item.id);

      // Only auto-select if there were previous items AND all were selected
      // We check against the Context's current selectedValues
      const allPreviousSelected = previousItemIds.length > 0 && previousItemIds.every((id) => selectedValues.includes(id));

      if (allPreviousSelected) {
        // Auto-select the new items too
        const newItemIds = filterItems.slice(previousLength).map((item) => item.id);
        setSelected(accessorKey, [...selectedValues, ...newItemIds]);
      }
    },
    [enabled, filterItems, selectedValues, accessorKey, setSelected]
  );

  return {
    selectedValues,
    setSelectedValues: (values: string[]) => setSelected(accessorKey, values),
    handleAutoSelectNewItems,
  };
};
